import React, { useEffect, useState } from 'react';
import {
  Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, Stack, Table, TableBody, TableCell, TableHead, TableRow,
  TextField, Typography,
} from '@mui/material';
import { KeyRound } from 'lucide-react';
import { listBranches } from '../../services/tierBranchApi';
import { getBranchManager, setBranchManager } from '../../services/bankingApi';
import type { Branch } from '../../types/tierBranch';
import type { BranchManager } from '../../types/banking';

/* Give a branch a login for the branch portal. Each branch maps to one
   BranchManager staff account (username + password). */
export default function BranchManagersTab() {
  const [branches, setBranches] = useState<Branch[]>([]);
  const [managers, setManagers] = useState<Record<number, BranchManager | null>>({});
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [branch, setBranch] = useState<Branch | null>(null);
  const [form, setForm] = useState({ username: '', email: '', password: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const res = await listBranches({ limit: 100 });
      setBranches(res.data);
      const entries = await Promise.all(
        res.data.map(async (b) => [b.id, (await getBranchManager(Number(b.id))).manager] as const)
      );
      setManagers(Object.fromEntries(entries));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openFor = (b: Branch) => {
    const m = managers[Number(b.id)];
    setBranch(b);
    setForm({ username: m?.username || '', email: m?.email || '', password: '' });
    setError(''); setOpen(true);
  };

  const save = async () => {
    if (!branch) return;
    setSaving(true); setError('');
    try {
      await setBranchManager(Number(branch.id), form);
      setOpen(false); await load();
    } catch (e: any) { setError(e.message || 'Save failed'); } finally { setSaving(false); }
  };

  if (loading) return <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box>;

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Each branch can have one manager login for the Branch portal at <b>/branch/login</b>.
      </Typography>
      <Table size="small">
        <TableHead>
          <TableRow>
            {['Branch', 'Manager', 'Status', ''].map((h) => (
              <TableCell key={h} sx={{ color: 'text.secondary' }}>{h}</TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {branches.map((b) => {
            const m = managers[Number(b.id)];
            return (
              <TableRow key={b.id} hover>
                <TableCell>{b.name}</TableCell>
                <TableCell>{m ? m.username : <Typography variant="body2" color="text.secondary">— none —</Typography>}</TableCell>
                <TableCell>{m && <Chip size="small" color={m.status === 'active' ? 'success' : 'error'} label={m.status} />}</TableCell>
                <TableCell align="right">
                  <Button size="small" startIcon={<KeyRound size={14} />} onClick={() => openFor(b)}>
                    {m ? 'Edit / Reset' : 'Create Login'}
                  </Button>
                </TableCell>
              </TableRow>
            );
          })}
          {!branches.length && (
            <TableRow><TableCell colSpan={4} align="center" sx={{ color: 'text.secondary', py: 3 }}>
              No branches yet.</TableCell></TableRow>
          )}
        </TableBody>
      </Table>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{branch?.name} — Manager Login</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label="Username" size="small" value={form.username}
              onChange={(e) => setForm((f) => ({ ...f, username: e.target.value }))} />
            <TextField label="Email (optional)" size="small" value={form.email}
              onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} />
            <TextField label={managers[Number(branch?.id)] ? 'New Password (leave blank to keep)' : 'Password'}
              type="password" size="small" value={form.password}
              onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))} />
            {error && <Typography color="error" variant="body2">{error}</Typography>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={save} disabled={saving || !form.username}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
